import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PriceChart } from '../PriceChart';
import { AddressBadge, TxHashBadge } from '../AddressBadge';
import { ChevronLeft, BarChart3, ArrowUp, ArrowDown } from 'lucide-react';
import { formatNumber, formatSmallPrice } from '../../lib/utils';

export const HistoricalDayView = ({ data, chart, swaps, loading, onExit, getSwapDirection }) => (
  <div className="space-y-8 animate-fade-in" data-testid="historical-dashboard">
    <Button variant="outline" onClick={onExit} className="mb-4" data-testid="historical-back-button">
      <ChevronLeft className="w-4 h-4 mr-2" />
      Back to Current Dashboard
    </Button>

    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
      <h1 className="text-2xl font-bold text-amber-600 mb-1">
        Historical View: {data.date}
      </h1>
      <p className="text-muted-foreground text-sm">
        Viewing all data as it was on {new Date(data.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20">
        <p className="text-sm text-muted-foreground mb-1">Open Price</p>
        <p className="text-xl font-bold text-emerald-600">{formatSmallPrice(data.price?.open)}</p>
      </div>
      <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
        <p className="text-sm text-muted-foreground mb-1">Close Price</p>
        <p className="text-xl font-bold text-blue-600">{formatSmallPrice(data.price?.close)}</p>
      </div>
      <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
        <p className="text-sm text-muted-foreground mb-1">High (ATH)</p>
        <p className="text-xl font-bold text-green-600">{formatSmallPrice(data.price?.high)}</p>
      </div>
      <div className="p-4 rounded-xl bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20">
        <p className="text-sm text-muted-foreground mb-1">Low (ATL)</p>
        <p className="text-xl font-bold text-red-600">{formatSmallPrice(data.price?.low)}</p>
      </div>
    </div>

    {/* TradingView Chart for Historical Day */}
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Price Chart - {data.date}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <PriceChart
          data={chart}
          height={350}
          showVolume={true}
          loading={loading}
        />
      </CardContent>
    </Card>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-violet-500/5 border border-violet-500/20">
        <p className="text-sm text-muted-foreground mb-1">Volume (USD)</p>
        <p className="text-xl font-bold text-violet-600">{data.totals?.volume_usd}</p>
      </div>
      <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20">
        <p className="text-sm text-muted-foreground mb-1">Total Swaps</p>
        <p className="text-xl font-bold text-amber-600">{data.totals?.swaps?.toLocaleString()}</p>
      </div>
      <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20">
        <p className="text-sm text-muted-foreground mb-1">KROWN Traded</p>
        <p className="text-xl font-bold text-cyan-600">{data.totals?.krown_traded}</p>
      </div>
      <div className="p-4 rounded-xl bg-gradient-to-br from-pink-500/10 to-pink-500/5 border border-pink-500/20">
        <p className="text-sm text-muted-foreground mb-1">Price Change</p>
        <p className={`text-xl font-bold ${(data.price?.change_percent || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {data.price?.change_percent !== null
            ? `${data.price.change_percent >= 0 ? '+' : ''}${data.price.change_percent.toFixed(2)}%`
            : 'N/A'
          }
        </p>
      </div>
    </div>

    {data.by_pool && data.by_pool.length > 0 && (
      <Card>
        <CardHeader><CardTitle>Volume by Pool</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.by_pool.map((pool) => (
              <div key={pool.pool_address} className="p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold">{pool.pair}</span>
                  <Badge variant="outline">{pool.swaps} swaps</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Volume: </span><span className="font-mono text-emerald-600">{pool.volume_usd}</span></div>
                  <div><span className="text-muted-foreground">KROWN: </span><span className="font-mono">{pool.volume_krown}</span></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}

    <Card>
      <CardHeader><CardTitle>Swaps on {data.date}</CardTitle></CardHeader>
      <CardContent>
        <div className="table-container rounded-lg border">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tx Hash</th>
                <th>Wallet</th>
                <th>Type</th>
                <th>Pool</th>
                <th className="text-right">KROWN Amount</th>
                <th className="text-right">Volume (USD)</th>
                <th className="text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {swaps.map((swap, idx) => {
                const direction = getSwapDirection(swap);
                return (
                  <tr key={`${swap.tx_hash}-${swap.log_index}-${idx}`}>
                    <td><TxHashBadge hash={swap.tx_hash} truncate /></td>
                    <td><AddressBadge address={swap.pool_address} truncate /></td>
                    <td>
                      <Badge className={direction === 'buy' ? 'bg-emerald-500/20 text-emerald-600' : 'bg-red-500/20 text-red-600'}>
                        {direction === 'buy' ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
                        {direction.toUpperCase()}
                      </Badge>
                    </td>
                    <td><Badge variant="outline" className="text-xs">{swap.token0_symbol}/{swap.token1_symbol}</Badge></td>
                    <td className="text-right font-mono">{formatNumber(parseFloat(swap.krown_amount || 0).toFixed(2))}</td>
                    <td className="text-right font-mono text-emerald-600">${formatNumber(parseFloat(swap.volume_usd || 0).toFixed(2))}</td>
                    <td className="text-right text-sm text-muted-foreground">{swap.block_time ? new Date(swap.block_time).toLocaleTimeString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  </div>
);
